import { useCallback, useEffect, useState } from 'react'

/**
 * Theme preference. The *applied* class is set by an inline script in
 * index.html before first paint, so there is never a flash; this module only
 * keeps React in sync with it afterwards.
 */
export const THEME_MODES = ['light', 'dark', 'system'] as const

/**
 * What an administrator sees before they choose.
 *
 * Light, not 'system'. Consistent with the public site, and it means a
 * screenshot in a bug report looks the same as what the next person sees.
 * The choice is remembered in localStorage once made.
 */
export const DEFAULT_THEME: ThemeMode = 'light'
export type ThemeMode = (typeof THEME_MODES)[number]

const STORAGE_KEY = 'avexa.admin.theme'

function readStored(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored && (THEME_MODES as readonly string[]).includes(stored)) return stored as ThemeMode
  } catch {
    // Storage unavailable — fall back to following the system.
  }
  return DEFAULT_THEME
}

function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') return prefersDark() ? 'dark' : 'light'
  return mode
}

function apply(mode: ThemeMode): void {
  const resolved = resolveTheme(mode)
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  root.style.colorScheme = resolved
}

export type UseThemeResult = {
  mode: ThemeMode
  resolved: 'light' | 'dark'
  setMode: (mode: ThemeMode) => void
}

export function useTheme(): UseThemeResult {
  const [mode, setModeState] = useState<ThemeMode>(readStored)
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolveTheme(readStored()))

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
    setResolved(resolveTheme(next))
    apply(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Non-fatal: the choice simply will not survive a reload.
    }
  }, [])

  // Follow the OS while the preference is "system".
  useEffect(() => {
    if (mode !== 'system') return undefined
    const query = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (): void => {
      setResolved(prefersDark() ? 'dark' : 'light')
      apply('system')
    }
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [mode])

  return { mode, resolved, setMode }
}
